import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Type,
  Image,
  ImagePlus,
  MousePointerClick,
  Minus,
  MoveVertical,
  Columns,
  PanelTop,
  PanelBottom,
  Sparkles,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Copy,
  Eye,
  Settings2,
  LayoutTemplate,
  X,
  MessageSquare,
  Link2,
} from 'lucide-react';
import {
  BLOCK_TYPES,
  createBlock,
  createEmptyDesign,
  createDefaultStyleDesign,
  renderDesignToHtml,
  applyPlaceholdersToHtml,
  defaultSettings,
  mergeDesignSettings,
  normalizeColumnsProps,
  resizeColumns,
  textToHtmlLines,
} from '../utils/emailDesign';
import { MediaPickerButton } from './MediaLibrarySettings';

const ALIGN_OPTS = [
  { value: 'left', label: 'Links' },
  { value: 'center', label: 'Mitte' },
  { value: 'right', label: 'Rechts' },
];

const ICONS = {
  header: PanelTop,
  hero: Sparkles,
  text: Type,
  image: Image,
  imageText: ImagePlus,
  button: MousePointerClick,
  columns: Columns,
  callout: MessageSquare,
  divider: Minus,
  spacer: MoveVertical,
  footer: PanelBottom,
};

function Field({ label, children }) {
  return (
    <div className="block space-y-1">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400">{label}</span>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, multiline = false, rows = 4, allowLinks = false }) {
  const inputRef = useRef(null);
  const cls = 'input-field text-sm';

  const insertLink = () => {
    const el = inputRef.current;
    const current = value || '';
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : current.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
    const selected = current.slice(start, end);
    const label = selected || 'Linktext';
    const url = window.prompt('Link-URL eingeben (https://…)', 'https://');
    if (url == null) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    const token = `[${label}](${trimmed})`;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        /* ignore */
      }
    });
  };

  if (multiline) {
    return (
      <div className="space-y-1">
        {allowLinks ? (
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-neutral-400">
              Links: Text markieren → Link, oder <span className="font-mono">[Text](https://…)</span>
            </p>
            <button
              type="button"
              onClick={insertLink}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/40 hover:bg-primary-100 dark:hover:bg-primary-950/70 border border-primary-200/70 dark:border-primary-800"
              title="Link einfügen"
            >
              <Link2 className="w-3 h-3" />
              Link
            </button>
          </div>
        ) : null}
        <textarea
          ref={inputRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className={`${cls} resize-y min-h-[5rem] max-h-36`}
          placeholder={placeholder}
        />
      </div>
    );
  }
  return (
    <div className={allowLinks ? 'space-y-1' : undefined}>
      {allowLinks ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={insertLink}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/40 hover:bg-primary-100 dark:hover:bg-primary-950/70 border border-primary-200/70 dark:border-primary-800"
            title="Link einfügen"
          >
            <Link2 className="w-3 h-3" />
            Link
          </button>
        </div>
      ) : null}
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={cls}
        placeholder={placeholder}
      />
    </div>
  );
}

function LinkedText({ value, fallback = '', className = '', style, linkColor }) {
  const html = textToHtmlLines(value || fallback, linkColor || '#60a5fa');
  if (!value && !fallback) return null;
  return (
    <div
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: html || escapePreviewFallback(fallback) }}
    />
  );
}

function escapePreviewFallback(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function ImageUrlInput({ value, onChange, placeholder = 'https://…' }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="input-field text-sm flex-1 min-w-0"
        placeholder={placeholder}
      />
      <MediaPickerButton label="Bibliothek" onSelect={onChange} />
    </div>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value && /^#/.test(value) ? value : '#3b82f6'}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-10 rounded-md border border-neutral-200 dark:border-neutral-700 bg-transparent cursor-pointer p-0.5"
      />
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="input-field text-sm font-mono flex-1"
        placeholder="#000000"
      />
    </div>
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="input-field text-sm">
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function NumberInput({ value, onChange, min = 0, max = 999, step = 1 }) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={Number.isFinite(Number(value)) ? Number(value) : ''}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(min);
          return;
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        onChange(Math.min(max, Math.max(min, n)));
      }}
      className="input-field text-sm"
    />
  );
}

function Row2({ children }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

function BlockPropsEditor({ block, onChange, variables = [], onInsertVar }) {
  const p = block.props || {};
  const set = (key, val) => onChange({ ...p, [key]: val });

  const varButtons =
    variables.length > 0 ? (
      <div className="flex flex-wrap gap-0.5 pt-0.5">
        {variables.slice(0, 12).map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => onInsertVar?.(v.key)}
            className="px-1 py-px rounded text-[8px] leading-tight font-mono bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-primary-50 dark:hover:bg-primary-950/40 hover:text-primary-700"
            title={v.label || v.key}
          >
            {`{{${v.key}}}`}
          </button>
        ))}
      </div>
    ) : null;

  switch (block.type) {
    case 'header':
      return (
        <div className="space-y-3">
          <Field label="Logo-URL">
            <ImageUrlInput value={p.logoUrl} onChange={(v) => set('logoUrl', v)} />
          </Field>
          {p.logoUrl ? (
            <Field label="Logo-Größe (%)">
              <NumberInput value={p.logoWidth ?? 28} onChange={(v) => set('logoWidth', v)} min={8} max={100} />
            </Field>
          ) : null}
          <Field label="Titel">
            <TextInput value={p.title} onChange={(v) => set('title', v)} />
            {varButtons}
          </Field>
          <Field label="Untertitel">
            <TextInput value={p.subtitle} onChange={(v) => set('subtitle', v)} />
          </Field>
          <Field label="Rechts-Link (Text)">
            <TextInput value={p.rightLinkLabel} onChange={(v) => set('rightLinkLabel', v)} placeholder="Mein Konto" />
          </Field>
          <Field label="Rechts-Link (URL)">
            <TextInput value={p.rightLinkUrl} onChange={(v) => set('rightLinkUrl', v)} placeholder="https://…" />
          </Field>
          <Row2>
            <Field label="Titel-Größe">
              <NumberInput value={p.titleSize} onChange={(v) => set('titleSize', v)} min={14} max={40} />
            </Field>
            <Field label="Untertitel-Größe">
              <NumberInput value={p.subtitleSize} onChange={(v) => set('subtitleSize', v)} min={10} max={22} />
            </Field>
          </Row2>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'center'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Field label="Hintergrund">
            <ColorInput value={p.backgroundColor} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Field label="Textfarbe">
            <ColorInput value={p.textColor} onChange={(v) => set('textColor', v)} />
          </Field>
          <Row2>
            <Field label="Padding oben/unten">
              <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={80} />
            </Field>
            <Field label="Padding links/rechts">
              <NumberInput value={p.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={80} />
            </Field>
          </Row2>
        </div>
      );
    case 'hero':
      return (
        <div className="space-y-3">
          <Field label="Bild-URL">
            <ImageUrlInput value={p.imageUrl} onChange={(v) => set('imageUrl', v)} />
          </Field>
          {p.imageUrl ? (
            <Field label="Bildgröße (%)">
              <NumberInput value={p.imageWidth ?? 100} onChange={(v) => set('imageWidth', v)} min={10} max={100} />
            </Field>
          ) : null}
          <Field label="Layout">
            <SelectInput
              value={p.layout || 'stacked'}
              onChange={(v) => set('layout', v)}
              options={[
                { value: 'stacked', label: 'Bild oben' },
                { value: 'split', label: 'Text links / Bild rechts' },
              ]}
            />
          </Field>
          <Field label="Eyebrow (klein, oben)">
            <TextInput value={p.eyebrow} onChange={(v) => set('eyebrow', v)} placeholder="HALLO {{contact_name}}" />
          </Field>
          <Field label="Eyebrow-Farbe">
            <ColorInput value={p.eyebrowColor || '#F04F2F'} onChange={(v) => set('eyebrowColor', v)} />
          </Field>
          <Field label="Headline">
            <TextInput value={p.headline} onChange={(v) => set('headline', v)} />
            {varButtons}
          </Field>
          <Field label="Text">
            <TextInput value={p.subtext} onChange={(v) => set('subtext', v)} multiline rows={3} allowLinks />
          </Field>
          <Row2>
            <Field label="Headline-Größe">
              <NumberInput value={p.headlineSize} onChange={(v) => set('headlineSize', v)} min={16} max={40} />
            </Field>
            <Field label="Text-Größe">
              <NumberInput value={p.subtextSize} onChange={(v) => set('subtextSize', v)} min={12} max={22} />
            </Field>
          </Row2>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'center'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              checked={p.showButton !== false}
              onChange={(e) => set('showButton', e.target.checked)}
              className="rounded border-neutral-300"
            />
            Button anzeigen
          </label>
          {p.showButton !== false && (
            <>
              <Field label="Button-Text">
                <TextInput value={p.buttonLabel} onChange={(v) => set('buttonLabel', v)} />
              </Field>
              <Field label="Button-Link">
                <TextInput value={p.buttonUrl} onChange={(v) => set('buttonUrl', v)} />
              </Field>
              <Field label="Button-Farbe">
                <ColorInput value={p.buttonColor} onChange={(v) => set('buttonColor', v)} />
              </Field>
              <Field label="Button-Textfarbe">
                <ColorInput value={p.buttonTextColor || '#ffffff'} onChange={(v) => set('buttonTextColor', v)} />
              </Field>
              <Field label="Button-Radius">
                <NumberInput value={p.buttonRadius ?? 999} onChange={(v) => set('buttonRadius', v)} min={0} max={999} />
              </Field>
            </>
          )}
          <Field label="Hintergrund">
            <ColorInput value={p.backgroundColor} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Field label="Textfarbe">
            <ColorInput value={p.textColor} onChange={(v) => set('textColor', v)} />
          </Field>
          <Row2>
            <Field label="Padding Y">
              <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={80} />
            </Field>
            <Field label="Padding X">
              <NumberInput value={p.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={80} />
            </Field>
          </Row2>
        </div>
      );
    case 'text':
      return (
        <div className="space-y-3">
          <Field label="Inhalt">
            <TextInput value={p.content} onChange={(v) => set('content', v)} multiline rows={8} allowLinks />
            {varButtons}
          </Field>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'left'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Row2>
            <Field label="Schriftgröße">
              <NumberInput value={p.fontSize} onChange={(v) => set('fontSize', v)} min={11} max={28} />
            </Field>
            <Field label="Schriftstärke">
              <SelectInput
                value={String(p.fontWeight || '400')}
                onChange={(v) => set('fontWeight', v)}
                options={[
                  { value: '400', label: 'Normal' },
                  { value: '600', label: 'Halbfett' },
                  { value: '700', label: 'Fett' },
                ]}
              />
            </Field>
          </Row2>
          <Field label="Textfarbe">
            <ColorInput value={p.textColor || '#e4e4e7'} onChange={(v) => set('textColor', v)} />
          </Field>
          <Field label="Hintergrund (optional)">
            <ColorInput value={p.backgroundColor || '#18181b'} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Row2>
            <Field label="Padding Y">
              <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={80} />
            </Field>
            <Field label="Padding X">
              <NumberInput value={p.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={80} />
            </Field>
          </Row2>
        </div>
      );
    case 'image':
      return (
        <div className="space-y-3">
          <Field label="Bild-URL">
            <ImageUrlInput value={p.imageUrl} onChange={(v) => set('imageUrl', v)} />
          </Field>
          <Field label="Alt-Text">
            <TextInput value={p.alt} onChange={(v) => set('alt', v)} />
          </Field>
          <Field label="Link (optional)">
            <TextInput value={p.linkUrl} onChange={(v) => set('linkUrl', v)} />
          </Field>
          <Row2>
            <Field label="Breite (%)">
              <NumberInput value={p.width} onChange={(v) => set('width', v)} min={20} max={100} />
            </Field>
            <Field label="Eckenradius">
              <NumberInput value={p.borderRadius} onChange={(v) => set('borderRadius', v)} min={0} max={40} />
            </Field>
          </Row2>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'center'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Row2>
            <Field label="Padding Y">
              <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={80} />
            </Field>
            <Field label="Padding X">
              <NumberInput value={p.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={80} />
            </Field>
          </Row2>
        </div>
      );
    case 'imageText':
      return (
        <div className="space-y-3">
          <Field label="Bild-URL">
            <ImageUrlInput value={p.imageUrl} onChange={(v) => set('imageUrl', v)} />
          </Field>
          <Field label="Layout">
            <SelectInput
              value={p.layout || 'imageLeft'}
              onChange={(v) => set('layout', v)}
              options={[
                { value: 'imageLeft', label: 'Bild links / Text rechts' },
                { value: 'imageRight', label: 'Text links / Bild rechts' },
                { value: 'imageTop', label: 'Bild oben / Text unten' },
                { value: 'imageBottom', label: 'Text oben / Bild unten' },
              ]}
            />
          </Field>
          <Field label="Überschrift">
            <TextInput value={p.title} onChange={(v) => set('title', v)} />
            {varButtons}
          </Field>
          <Field label="Text">
            <TextInput value={p.text} onChange={(v) => set('text', v)} multiline rows={4} allowLinks />
          </Field>
          {p.layout !== 'imageTop' && p.layout !== 'imageBottom' && (
            <Field label="Spaltenanteil Bild (%)">
              <NumberInput value={p.imageWidth} onChange={(v) => set('imageWidth', v)} min={25} max={60} />
            </Field>
          )}
          <Field label="Bildgröße in Zelle (%)">
            <NumberInput value={p.imageScale ?? 100} onChange={(v) => set('imageScale', v)} min={10} max={100} />
          </Field>
          <Row2>
            <Field label="Titel-Größe">
              <NumberInput value={p.titleSize} onChange={(v) => set('titleSize', v)} min={12} max={32} />
            </Field>
            <Field label="Text-Größe">
              <NumberInput value={p.textSize} onChange={(v) => set('textSize', v)} min={11} max={20} />
            </Field>
          </Row2>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'left'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Field label="Hintergrund">
            <ColorInput value={p.backgroundColor || '#ffffff'} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Field label="Titel-Farbe">
            <ColorInput value={p.textColor || '#111827'} onChange={(v) => set('textColor', v)} />
          </Field>
          <Field label="Text-Farbe">
            <ColorInput value={p.mutedColor || '#6B7280'} onChange={(v) => set('mutedColor', v)} />
          </Field>
          <Field label="Button-Text (optional)">
            <TextInput value={p.buttonLabel} onChange={(v) => set('buttonLabel', v)} />
          </Field>
          <Field label="Button-URL">
            <TextInput value={p.buttonUrl} onChange={(v) => set('buttonUrl', v)} />
          </Field>
          <Row2>
            <Field label="Padding Y">
              <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={64} />
            </Field>
            <Field label="Padding X">
              <NumberInput value={p.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={64} />
            </Field>
          </Row2>
        </div>
      );
    case 'button':
      return (
        <div className="space-y-3">
          <Field label="Beschriftung">
            <TextInput value={p.label} onChange={(v) => set('label', v)} />
            {varButtons}
          </Field>
          <Field label="URL">
            <TextInput value={p.url} onChange={(v) => set('url', v)} />
          </Field>
          <Field label="Hintergrund">
            <ColorInput value={p.backgroundColor} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Field label="Textfarbe">
            <ColorInput value={p.textColor} onChange={(v) => set('textColor', v)} />
          </Field>
          <Row2>
            <Field label="Schriftgröße">
              <NumberInput value={p.fontSize} onChange={(v) => set('fontSize', v)} min={11} max={22} />
            </Field>
            <Field label="Eckenradius">
              <NumberInput value={p.borderRadius} onChange={(v) => set('borderRadius', v)} min={0} max={999} />
            </Field>
          </Row2>
          <Row2>
            <Field label="Innenabstand Y">
              <NumberInput value={p.buttonPaddingY} onChange={(v) => set('buttonPaddingY', v)} min={4} max={32} />
            </Field>
            <Field label="Innenabstand X">
              <NumberInput value={p.buttonPaddingX} onChange={(v) => set('buttonPaddingX', v)} min={8} max={48} />
            </Field>
          </Row2>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'center'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Field label="Abstand um den Button">
            <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={64} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              checked={!!p.fullWidth}
              onChange={(e) => set('fullWidth', e.target.checked)}
              className="rounded border-neutral-300"
            />
            Volle Breite
          </label>
        </div>
      );
    case 'columns': {
      const cols = normalizeColumnsProps(p);
      const updateItem = (index, key, val) => {
        const items = cols.items.map((it, i) => (i === index ? { ...it, [key]: val } : it));
        onChange({ ...cols, items });
      };
      return (
        <div className="space-y-3">
          <Field label="Anzahl Spalten">
            <SelectInput
              value={String(cols.columnCount)}
              onChange={(v) => onChange(resizeColumns(cols, Number(v)))}
              options={[
                { value: '2', label: '2 Spalten' },
                { value: '3', label: '3 Spalten' },
                { value: '4', label: '4 Spalten' },
              ]}
            />
          </Field>
          {cols.items.map((item, index) => (
            <div
              key={index}
              className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-2.5 space-y-2"
            >
              <p className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">
                Spalte {index + 1}
              </p>
              <Field label="Titel">
                <TextInput value={item.title} onChange={(v) => updateItem(index, 'title', v)} />
              </Field>
              <Field label="Label (klein, oben)">
                <TextInput value={item.label} onChange={(v) => updateItem(index, 'label', v)} placeholder="ANFRAGE ID" />
              </Field>
              <Field label="Bild-URL (optional)">
                <ImageUrlInput value={item.imageUrl} onChange={(v) => updateItem(index, 'imageUrl', v)} />
              </Field>
              <Field label="Text">
                <TextInput value={item.text} onChange={(v) => updateItem(index, 'text', v)} multiline rows={3} allowLinks />
              </Field>
            </div>
          ))}
          {varButtons}
          <Field label="Ausrichtung">
            <SelectInput value={cols.align || 'left'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Field label="Bildgröße in Spalte (%)">
            <NumberInput value={cols.imageSize ?? 40} onChange={(v) => set('imageSize', v)} min={8} max={100} />
          </Field>
          <Row2>
            <Field label="Titel-Größe">
              <NumberInput value={cols.titleSize} onChange={(v) => set('titleSize', v)} min={12} max={28} />
            </Field>
            <Field label="Text-Größe">
              <NumberInput value={cols.textSize} onChange={(v) => set('textSize', v)} min={11} max={20} />
            </Field>
          </Row2>
          <Field label="Abstand zwischen Spalten">
            <NumberInput value={cols.gap} onChange={(v) => set('gap', v)} min={0} max={24} />
          </Field>
          <Field label="Hintergrund">
            <ColorInput value={cols.backgroundColor} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Field label="Textfarbe">
            <ColorInput value={cols.textColor} onChange={(v) => set('textColor', v)} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              checked={!!cols.bordered}
              onChange={(e) => set('bordered', e.target.checked)}
              className="rounded border-neutral-300"
            />
            Rahmen / Karte
          </label>
          <Row2>
            <Field label="Padding Y">
              <NumberInput value={cols.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={64} />
            </Field>
            <Field label="Padding X">
              <NumberInput value={cols.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={64} />
            </Field>
          </Row2>
        </div>
      );
    }
    case 'callout':
      return (
        <div className="space-y-3">
          <Field label="Titel">
            <TextInput value={p.title} onChange={(v) => set('title', v)} />
            {varButtons}
          </Field>
          <Field label="Text">
            <TextInput value={p.text} onChange={(v) => set('text', v)} multiline rows={4} allowLinks />
          </Field>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'left'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Field label="Hintergrund">
            <ColorInput value={p.backgroundColor} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Field label="Titel-Farbe">
            <ColorInput value={p.textColor} onChange={(v) => set('textColor', v)} />
          </Field>
          <Field label="Text-Farbe">
            <ColorInput value={p.mutedColor || '#6B7280'} onChange={(v) => set('mutedColor', v)} />
          </Field>
          <Field label="Eckenradius">
            <NumberInput value={p.borderRadius} onChange={(v) => set('borderRadius', v)} min={0} max={24} />
          </Field>
          <Field label="Button-Text (optional)">
            <TextInput value={p.buttonLabel} onChange={(v) => set('buttonLabel', v)} />
          </Field>
          <Field label="Button-URL">
            <TextInput value={p.buttonUrl} onChange={(v) => set('buttonUrl', v)} />
          </Field>
          <Field label="Button-Stil">
            <SelectInput
              value={p.buttonStyle || 'solid'}
              onChange={(v) => set('buttonStyle', v)}
              options={[
                { value: 'solid', label: 'Gefüllt' },
                { value: 'outline', label: 'Outline' },
              ]}
            />
          </Field>
        </div>
      );
    case 'divider':
      return (
        <div className="space-y-3">
          <Field label="Farbe">
            <ColorInput value={p.color} onChange={(v) => set('color', v)} />
          </Field>
          <Field label="Stärke (px)">
            <NumberInput value={p.thickness} onChange={(v) => set('thickness', v)} min={1} max={8} />
          </Field>
          <Row2>
            <Field label="Padding Y">
              <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={48} />
            </Field>
            <Field label="Padding X">
              <NumberInput value={p.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={48} />
            </Field>
          </Row2>
        </div>
      );
    case 'spacer':
      return (
        <div className="space-y-3">
          <Field label="Höhe (px)">
            <NumberInput value={p.height} onChange={(v) => set('height', v)} min={8} max={120} />
          </Field>
        </div>
      );
    case 'footer':
      return (
        <div className="space-y-3">
          <Field label="Inhalt">
            <TextInput value={p.content} onChange={(v) => set('content', v)} multiline rows={5} allowLinks />
            {varButtons}
          </Field>
          <Field label="Ausrichtung">
            <SelectInput value={p.align || 'center'} onChange={(v) => set('align', v)} options={ALIGN_OPTS} />
          </Field>
          <Field label="Schriftgröße">
            <NumberInput value={p.fontSize} onChange={(v) => set('fontSize', v)} min={10} max={16} />
          </Field>
          <Field label="Hintergrund">
            <ColorInput value={p.backgroundColor} onChange={(v) => set('backgroundColor', v)} />
          </Field>
          <Field label="Textfarbe">
            <ColorInput value={p.textColor} onChange={(v) => set('textColor', v)} />
          </Field>
          <Row2>
            <Field label="Padding Y">
              <NumberInput value={p.paddingY} onChange={(v) => set('paddingY', v)} min={0} max={64} />
            </Field>
            <Field label="Padding X">
              <NumberInput value={p.paddingX} onChange={(v) => set('paddingX', v)} min={0} max={64} />
            </Field>
          </Row2>
        </div>
      );
    default:
      return <p className="text-sm text-neutral-500">Keine Einstellungen</p>;
  }
}

function CanvasBlockPreview({ block, settings = {} }) {
  const p = block.props || {};
  const baseText = settings.textColor || '#f4f4f5';
  const mutedText = '#a1a1aa';
  const contentBg = settings.contentBg || '#18181b';
  const linkColor = settings.linkColor || '#60a5fa';
  const px = (n, fallback) => {
    const v = Number(n);
    return Number.isFinite(v) ? v : fallback;
  };

  switch (block.type) {
    case 'header': {
      const align = p.align || 'center';
      const hasRight = !!p.rightLinkLabel;
      return (
        <div
          className={`px-4 py-4 flex items-center gap-3 ${
            hasRight ? 'justify-between' : align === 'right' ? 'justify-end' : align === 'left' ? 'justify-start' : 'justify-center'
          }`}
          style={{
            background: p.backgroundColor || '#ffffff',
            color: p.textColor || '#111827',
            paddingTop: px(p.paddingY, 16),
            paddingBottom: px(p.paddingY, 16),
            paddingLeft: px(p.paddingX, 16),
            paddingRight: px(p.paddingX, 16),
            textAlign: hasRight ? 'left' : align,
          }}
        >
          <div style={{ textAlign: hasRight ? 'left' : align }}>
            {p.logoUrl ? (
              <img
                src={p.logoUrl}
                alt=""
                className="mb-1"
                style={{
                  display: 'block',
                  width: `${Math.min(100, Math.max(8, px(p.logoWidth, 28)))}%`,
                  maxWidth: '100%',
                  height: 'auto',
                  margin: hasRight || align === 'left' ? '0' : align === 'right' ? '0 0 0 auto' : '0 auto',
                }}
              />
            ) : null}
            <div
              className="font-extrabold tracking-wide"
              style={{ fontSize: px(p.titleSize, 18) }}
            >
              {p.title || 'Kopfzeile'}
            </div>
            {p.subtitle ? (
              <div className="opacity-70 mt-0.5" style={{ fontSize: px(p.subtitleSize, 13) }}>
                {p.subtitle}
              </div>
            ) : null}
          </div>
          {hasRight ? (
            <span className="text-xs flex-shrink-0" style={{ color: '#6B7280' }}>{p.rightLinkLabel}</span>
          ) : null}
        </div>
      );
    }
    case 'hero': {
      const align = p.align || 'center';
      const imgPct = Math.min(100, Math.max(10, px(p.imageWidth, 100)));
      const textBlock = (
        <div className="px-4 py-3" style={{ textAlign: align }}>
          {p.eyebrow ? (
            <div
              className="font-bold tracking-widest uppercase mb-1"
              style={{ color: p.eyebrowColor || '#F04F2F', fontSize: 10 }}
            >
              {p.eyebrow}
            </div>
          ) : null}
          <div className="font-bold" style={{ color: p.textColor || baseText, fontSize: px(p.headlineSize, 22), lineHeight: 1.25 }}>
            {p.headline}
          </div>
          <div className="mt-1 line-clamp-3" style={{ color: mutedText, fontSize: px(p.subtextSize, 14) }}>
            <LinkedText value={p.subtext} linkColor={linkColor} />
          </div>
          {p.showButton !== false && p.buttonLabel ? (
            <span
              className="inline-block mt-2 text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ background: p.buttonColor || '#F04F2F', color: p.buttonTextColor || '#ffffff' }}
            >
              {p.buttonLabel}
            </span>
          ) : null}
        </div>
      );
      const heroImg = p.imageUrl ? (
        <img
          src={p.imageUrl}
          alt=""
          style={{
            display: 'block',
            width: `${imgPct}%`,
            maxWidth: '100%',
            height: 'auto',
            margin: '0 auto',
            background: 'transparent',
          }}
        />
      ) : (
        !p.layout || p.layout === 'stacked' ? null : (
          <div className="h-28" style={{ background: '#F3F4F6' }} />
        )
      );
      return (
        <div
          className={p.layout === 'split' ? 'grid grid-cols-2 gap-2 items-center' : ''}
          style={{ background: p.backgroundColor || contentBg, color: p.textColor || baseText }}
        >
          {textBlock}
          {heroImg}
        </div>
      );
    }
    case 'text':
      return (
        <LinkedText
          value={p.content}
          fallback="Text…"
          className="line-clamp-6"
          linkColor={linkColor}
          style={{
            color: p.textColor || baseText,
            fontSize: px(p.fontSize, 15),
            fontWeight: p.fontWeight || 400,
            textAlign: p.align || 'left',
            background: p.backgroundColor || undefined,
            paddingTop: px(p.paddingY, 8),
            paddingBottom: px(p.paddingY, 8),
            paddingLeft: px(p.paddingX, 16),
            paddingRight: px(p.paddingX, 16),
          }}
        />
      );
    case 'image': {
      const radius = px(p.borderRadius, 0);
      const widthPct = Math.min(100, Math.max(5, px(p.width, 100)));
      return (
        <div
          style={{
            textAlign: p.align || 'center',
            paddingTop: px(p.paddingY, 12),
            paddingBottom: px(p.paddingY, 12),
            paddingLeft: px(p.paddingX, 16),
            paddingRight: px(p.paddingX, 16),
          }}
        >
          {p.imageUrl ? (
            <img
              src={p.imageUrl}
              alt={p.alt || ''}
              style={{
                display: 'inline-block',
                width: `${widthPct}%`,
                maxWidth: '100%',
                height: 'auto',
                borderRadius: radius,
                verticalAlign: 'middle',
              }}
            />
          ) : (
            <div
              className="h-24 inline-flex items-center justify-center text-xs"
              style={{
                background: '#27272a',
                color: mutedText,
                width: `${widthPct}%`,
                borderRadius: radius,
              }}
            >
              Bild-URL setzen
            </div>
          )}
        </div>
      );
    }
    case 'imageText': {
      const layout = p.layout || 'imageLeft';
      const stacked = layout === 'imageTop' || layout === 'imageBottom';
      const radius = px(p.borderRadius, 8);
      const imgScale = Math.min(100, Math.max(10, px(p.imageScale, 100)));
      const imageEl = p.imageUrl ? (
        <img
          src={p.imageUrl}
          alt=""
          style={{
            display: 'block',
            width: `${imgScale}%`,
            maxWidth: '100%',
            height: 'auto',
            borderRadius: radius,
            margin: '0 auto',
          }}
        />
      ) : (
        <div className="h-24" style={{ background: '#E5E7EB', borderRadius: radius }} />
      );
      const textEl = (
        <div style={{ textAlign: p.align || 'left' }}>
          <div className="font-semibold" style={{ color: p.textColor || baseText, fontSize: px(p.titleSize, 18) }}>
            {p.title}
          </div>
          <div className="mt-1 line-clamp-3" style={{ color: p.mutedColor || mutedText, fontSize: px(p.textSize, 14) }}>
            {p.text}
          </div>
        </div>
      );
      return (
        <div
          className={stacked ? 'flex flex-col gap-2' : 'grid grid-cols-2 items-center gap-2'}
          style={{
            background: p.backgroundColor || contentBg,
            paddingTop: px(p.paddingY, 12),
            paddingBottom: px(p.paddingY, 12),
            paddingLeft: px(p.paddingX, 12),
            paddingRight: px(p.paddingX, 12),
          }}
        >
          {layout === 'imageBottom' ? (
            <>
              {textEl}
              {imageEl}
            </>
          ) : layout === 'imageRight' ? (
            <>
              {textEl}
              {imageEl}
            </>
          ) : (
            <>
              {imageEl}
              {textEl}
            </>
          )}
        </div>
      );
    }
    case 'button':
      return (
        <div className={`px-4 py-3 flex ${p.align === 'left' ? 'justify-start' : p.align === 'right' ? 'justify-end' : 'justify-center'}`}>
          <span
            className="font-semibold px-4 py-2"
            style={{
              background: p.backgroundColor || '#3b82f6',
              color: p.textColor || '#ffffff',
              fontSize: px(p.fontSize, 14),
              borderRadius: px(p.borderRadius, 6),
            }}
          >
            {p.label || 'Button'}
          </span>
        </div>
      );
    case 'columns': {
      const cols = normalizeColumnsProps(p);
      const gridCols =
        cols.columnCount === 4
          ? 'grid-cols-4'
          : cols.columnCount === 3
            ? 'grid-cols-3'
            : 'grid-cols-2';
      return (
        <div
          className={`grid ${gridCols} gap-2 mx-2 my-1`}
          style={{
            color: cols.textColor || baseText,
            background: cols.backgroundColor || contentBg,
            border: cols.bordered ? `1px solid ${cols.borderColor || '#E5E7EB'}` : undefined,
            borderRadius: cols.bordered ? (cols.borderRadius || 12) : undefined,
            padding: 8,
            textAlign: cols.align || 'left',
          }}
        >
          {cols.items.map((item, i) => (
            <div key={i}>
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt=""
                  className="mb-1"
                  style={{
                    display: 'block',
                    width: `${Math.min(100, Math.max(8, px(cols.imageSize, 40)))}%`,
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 8,
                    margin: cols.align === 'center' ? '0 auto' : cols.align === 'right' ? '0 0 0 auto' : '0',
                  }}
                />
              ) : null}
              {item.label ? (
                <div className="font-bold tracking-wide uppercase mb-0.5" style={{ color: cols.labelColor || mutedText, fontSize: 9 }}>
                  {item.label}
                </div>
              ) : null}
              {item.title ? (
                <div className="font-semibold" style={{ fontSize: px(cols.titleSize, 15) }}>{item.title}</div>
              ) : null}
              {item.text ? (
                <div className="line-clamp-3" style={{ color: mutedText, fontSize: px(cols.textSize, 13) }}>{item.text}</div>
              ) : null}
            </div>
          ))}
        </div>
      );
    }
    case 'callout':
      return (
        <div className="px-3 py-2">
          <div
            className="px-3 py-3"
            style={{
              background: p.backgroundColor || '#F3F4F6',
              color: p.textColor || baseText,
              textAlign: p.align || 'left',
              borderRadius: px(p.borderRadius, 12),
            }}
          >
            <div className="font-semibold" style={{ fontSize: px(p.titleSize, 15) }}>{p.title}</div>
            <div className="mt-1 line-clamp-3" style={{ color: p.mutedColor || mutedText, fontSize: px(p.textSize, 13) }}>{p.text}</div>
            {p.buttonLabel ? (
              <span
                className="inline-block mt-2 text-[10px] font-semibold px-2.5 py-1 rounded-full"
                style={{
                  background: p.buttonStyle === 'outline' ? 'transparent' : (p.buttonColor || '#F04F2F'),
                  color: p.buttonTextColor || '#fff',
                  border: p.buttonStyle === 'outline' ? `1px solid ${p.buttonColor || '#fff'}` : undefined,
                }}
              >
                {p.buttonLabel}
              </span>
            ) : null}
          </div>
        </div>
      );
    case 'divider':
      return (
        <div className="px-4 py-3">
          <div style={{ borderTop: `${p.thickness || 1}px solid ${p.color || '#3f3f46'}` }} />
        </div>
      );
    case 'spacer':
      return (
        <div
          className="flex items-center justify-center text-[10px]"
          style={{
            height: Math.min(48, Math.max(16, (p.height || 24) / 2)),
            color: mutedText,
            background: contentBg,
          }}
        >
          {p.height || 24}px
        </div>
      );
    case 'footer':
      return (
        <LinkedText
          value={p.content}
          fallback="Fußzeile"
          className="px-4 py-3 text-center"
          linkColor={linkColor}
          style={{
            background: p.backgroundColor || '#09090b',
            color: p.textColor || mutedText,
            fontSize: px(p.fontSize, 12),
            textAlign: p.align || 'center',
          }}
        />
      );
    default:
      return null;
  }
}

/**
 * @param {{
 *   value: object|null,
 *   onChange: (design: object) => void,
 *   variables?: Array<{key:string,label?:string}>,
 *   placeholderMap?: Record<string,string>,
 *   className?: string,
 * }} props
 */
const EmailDesignBuilder = ({ value, onChange, variables = [], placeholderMap = {}, className = '' }) => {
  const design = value || createEmptyDesign();
  const [selectedId, setSelectedId] = useState(design.blocks[0]?.id || null);
  const [rightTab, setRightTab] = useState('block'); // block | settings
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [mobilePanel, setMobilePanel] = useState('canvas'); // blocks | canvas | edit
  const selected = design.blocks.find((b) => b.id === selectedId) || null;

  const previewHtml = useMemo(() => {
    const html = renderDesignToHtml(design);
    return applyPlaceholdersToHtml(html, placeholderMap);
  }, [design, placeholderMap]);

  const previewPageBg = String(design?.settings?.backgroundColor || '').trim() || '#09090b';

  const updateDesign = (next) => onChange(next);

  const updateSettings = (patch) => {
    updateDesign({
      ...design,
      settings: mergeDesignSettings({ ...design.settings, ...patch }),
    });
  };

  const updateBlockProps = (props) => {
    if (!selected) return;
    updateDesign({
      ...design,
      blocks: design.blocks.map((b) => (b.id === selected.id ? { ...b, props } : b)),
    });
  };

  const addBlock = (type) => {
    const block = createBlock(type);
    const idx = selected
      ? design.blocks.findIndex((b) => b.id === selected.id) + 1
      : design.blocks.length;
    const blocks = [...design.blocks];
    blocks.splice(idx, 0, block);
    updateDesign({ ...design, blocks });
    setSelectedId(block.id);
    setRightTab('block');
    setMobilePanel('edit');
  };

  const removeBlock = (id) => {
    const blocks = design.blocks.filter((b) => b.id !== id);
    updateDesign({ ...design, blocks });
    if (selectedId === id) setSelectedId(blocks[0]?.id || null);
  };

  const duplicateBlock = (id) => {
    const idx = design.blocks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const src = design.blocks[idx];
    const copy = createBlock(src.type);
    copy.props = { ...src.props };
    const blocks = [...design.blocks];
    blocks.splice(idx + 1, 0, copy);
    updateDesign({ ...design, blocks });
    setSelectedId(copy.id);
  };

  const moveBlock = (id, dir) => {
    const idx = design.blocks.findIndex((b) => b.id === id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= design.blocks.length) return;
    const blocks = [...design.blocks];
    const [item] = blocks.splice(idx, 1);
    blocks.splice(next, 0, item);
    updateDesign({ ...design, blocks });
  };

  const insertVarIntoSelected = (key) => {
    if (!selected) return;
    const token = `{{${key}}}`;
    if (selected.type === 'columns') {
      const cols = normalizeColumnsProps(selected.props);
      const items = cols.items.map((it, i) =>
        i === 0 ? { ...it, text: `${it.text || ''}${token}` } : it
      );
      updateBlockProps({ ...cols, items });
      return;
    }
    const p = { ...selected.props };
    const textKeys = ['content', 'title', 'subtitle', 'headline', 'subtext', 'label', 'text'];
    const target = textKeys.find((k) => k in p) || 'content';
    if (!(target in p)) p.content = token;
    else p[target] = `${p[target] || ''}${token}`;
    updateBlockProps(p);
  };

  const contentWidthPx = Math.min(720, Math.max(320, Number(design.settings?.contentWidth) || 600));

  return (
    <div className={`email-design-builder rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-neutral-50 dark:bg-neutral-950/50 flex flex-col min-h-0 h-full ${className}`.trim()}>
      {/* Mobile: Panel-Umschalter */}
      <div className="xl:hidden flex-shrink-0 grid grid-cols-3 border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        {[
          { id: 'blocks', label: 'Blöcke' },
          { id: 'canvas', label: 'E-Mail' },
          { id: 'edit', label: 'Bearbeiten' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setMobilePanel(tab.id)}
            className={`px-2 py-2.5 text-[11px] font-medium transition-colors ${
              mobilePanel === tab.id
                ? 'text-primary-700 dark:text-primary-300 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-950/20'
                : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col xl:flex-row flex-1 min-h-0">
        {/* Palette */}
        <aside
          className={`xl:w-44 flex-shrink-0 border-b xl:border-b-0 xl:border-r border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-2 overflow-y-auto min-h-0 ${
            mobilePanel === 'blocks' ? 'flex flex-col flex-1' : 'hidden'
          } xl:flex xl:flex-col xl:flex-none`}
        >
          <p className="px-2 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
            Blöcke
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-1 gap-1">
            {BLOCK_TYPES.map((bt) => {
              const Icon = ICONS[bt.type] || LayoutTemplate;
              return (
                <button
                  key={bt.type}
                  type="button"
                  onClick={() => addBlock(bt.type)}
                  className="flex items-center gap-2 px-2 py-2.5 sm:py-2 rounded-lg text-left text-xs text-neutral-700 dark:text-neutral-300 hover:bg-primary-50 dark:hover:bg-primary-950/30 hover:text-primary-700 transition-colors"
                  title={bt.description}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
                  <span className="truncate font-medium">{bt.label}</span>
                  <Plus className="w-3 h-3 ml-auto opacity-40 hidden xl:block" />
                </button>
              );
            })}
          </div>
        </aside>

        {/* Canvas — einzige Scrollfläche für die E-Mail */}
        <div
          className={`flex-1 min-w-0 min-h-0 overflow-y-auto p-2 sm:p-3 xl:p-4 ${
            mobilePanel === 'canvas' ? 'block' : 'hidden'
          } xl:block`}
          style={{ background: design.settings?.backgroundColor || '#09090b' }}
        >
          <div className="mb-2 flex items-center justify-center">
            <span className="text-[10px] tabular-nums text-neutral-500 bg-neutral-900/50 px-2 py-0.5 rounded">
              Breite {contentWidthPx}px
            </span>
          </div>
          <div
            className="mx-auto shadow-sm border border-zinc-700/80 overflow-hidden"
            style={{
              width: '100%',
              maxWidth: contentWidthPx,
              background: design.settings?.contentBg || '#18181b',
              color: design.settings?.textColor || '#f4f4f5',
            }}
          >
            {design.blocks.length === 0 ? (
              <div className="px-6 py-16 text-center text-sm text-neutral-500">
                Noch keine Blöcke — unter „Blöcke“ etwas hinzufügen.
              </div>
            ) : (
              design.blocks.map((block) => {
                const meta = BLOCK_TYPES.find((b) => b.type === block.type);
                const active = block.id === selectedId;
                return (
                  <div
                    key={block.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedId(block.id);
                      setRightTab('block');
                      setMobilePanel('edit');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedId(block.id);
                        setRightTab('block');
                        setMobilePanel('edit');
                      }
                    }}
                    className={`relative group border-2 transition-colors cursor-pointer ${
                      active
                        ? 'border-primary-500 ring-1 ring-primary-500/30'
                        : 'border-transparent hover:border-primary-300/60'
                    }`}
                  >
                    <div className="absolute top-1 left-1 z-10 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-neutral-900/80 text-white">
                        {meta?.label || block.type}
                      </span>
                    </div>
                    <div className="absolute top-1 right-1 z-10 flex gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        className="p-1 rounded bg-white shadow border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveBlock(block.id, -1);
                        }}
                        title="Nach oben"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        className="p-1 rounded bg-white shadow border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveBlock(block.id, 1);
                        }}
                        title="Nach unten"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        className="p-1 rounded bg-white shadow border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateBlock(block.id);
                        }}
                        title="Duplizieren"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        className="p-1 rounded bg-white shadow border border-danger-200 text-danger-600 hover:bg-danger-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeBlock(block.id);
                        }}
                        title="Löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <CanvasBlockPreview block={block} settings={design.settings} />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Inspector — feste Breite auf Desktop, nicht mitwachsen */}
        <aside
          className={`xl:w-96 xl:max-w-96 2xl:w-[28rem] 2xl:max-w-[28rem] flex-shrink-0 border-t xl:border-t-0 xl:border-l border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 flex-col min-h-0 ${
            mobilePanel === 'edit' ? 'flex flex-1 xl:flex-none' : 'hidden'
          } xl:flex`}
        >
          <div className="flex border-b border-neutral-200 dark:border-neutral-700">
            {[
              { id: 'block', label: 'Block', icon: LayoutTemplate },
              { id: 'settings', label: 'Stil', icon: Settings2 },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setRightTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors ${
                    rightTab === tab.id
                      ? 'text-primary-700 dark:text-primary-300 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-950/20'
                      : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setShowPreviewModal(true)}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium text-neutral-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              title="E-Mail-Vorschau öffnen"
            >
              <Eye className="w-3.5 h-3.5" />
              Vorschau
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            {rightTab === 'block' && (
              selected ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-neutral-800 dark:text-neutral-200">
                    {BLOCK_TYPES.find((b) => b.type === selected.type)?.label || selected.type}
                  </p>
                  <BlockPropsEditor
                    block={selected}
                    onChange={updateBlockProps}
                    variables={variables}
                    onInsertVar={insertVarIntoSelected}
                  />
                </div>
              ) : (
                <p className="text-sm text-neutral-500">Block auswählen oder hinzufügen.</p>
              )
            )}

            {rightTab === 'settings' && (
              <div className="space-y-3">
                <Field label="Seiten-Hintergrund">
                  <ColorInput
                    value={design.settings?.backgroundColor}
                    onChange={(v) => updateSettings({ backgroundColor: v })}
                  />
                </Field>
                <Field label="Inhalts-Hintergrund">
                  <ColorInput
                    value={design.settings?.contentBg}
                    onChange={(v) => updateSettings({ contentBg: v })}
                  />
                </Field>
                <Field label="Standard-Textfarbe">
                  <ColorInput
                    value={design.settings?.textColor}
                    onChange={(v) => updateSettings({ textColor: v })}
                  />
                </Field>
                <Field label="Linkfarbe">
                  <ColorInput
                    value={design.settings?.linkColor || defaultSettings().linkColor}
                    onChange={(v) => updateSettings({ linkColor: v })}
                  />
                </Field>
                <Field label="Breite (px)">
                  <NumberInput
                    value={design.settings?.contentWidth}
                    onChange={(v) => updateSettings({ contentWidth: v })}
                    min={320}
                    max={720}
                  />
                  <p className="mt-1 text-[10px] text-neutral-400 leading-snug">
                    In der Vorschau max. so breit wie das Fenster. Enger (z.&nbsp;B. 320–480) siehst du sofort.
                  </p>
                </Field>
                <Field label="Schriftart">
                  <SelectInput
                    value={design.settings?.fontFamily || defaultSettings().fontFamily}
                    onChange={(v) => updateSettings({ fontFamily: v })}
                    options={[
                      { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
                      { value: "Georgia, 'Times New Roman', serif", label: 'Georgia' },
                      { value: "'Trebuchet MS', Helvetica, sans-serif", label: 'Trebuchet' },
                      { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
                    ]}
                  />
                </Field>
                <button
                  type="button"
                  className="btn-secondary text-xs w-full mt-2"
                  onClick={() => {
                    if (window.confirm('Beispielvorlage laden? Aktuelles Design wird ersetzt.')) {
                      const next = createDefaultStyleDesign();
                      updateDesign(next);
                      setSelectedId(next.blocks[0]?.id || null);
                    }
                  }}
                >
                  Beispielvorlage laden
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>

      {showPreviewModal &&
        createPortal(
          <div className="fixed inset-0 z-[180] flex items-center justify-center p-3 sm:p-5">
            <div
              className="absolute inset-0 bg-neutral-950/75 backdrop-blur-sm"
              onClick={() => setShowPreviewModal(false)}
            />
            <div className="relative w-full max-w-3xl max-h-[94vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-neutral-700 bg-neutral-900 scale-in">
              <div className="flex-shrink-0 flex items-center justify-between gap-3 px-5 py-3.5 border-b border-neutral-800">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-white truncate">E-Mail-Vorschau</h3>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    So erscheint die Mail bei Empfängern (mit Beispieldaten)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(false)}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800 flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden bg-neutral-950 p-3 sm:p-4">
                <iframe
                  title="E-Mail Vorschau"
                  srcDoc={previewHtml}
                  className="w-full h-[min(72vh,40rem)] rounded-xl border border-neutral-800"
                  style={{ backgroundColor: previewPageBg }}
                  sandbox=""
                />
              </div>
              <div className="flex-shrink-0 px-5 py-3 border-t border-neutral-800 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(false)}
                  className="btn-primary"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default EmailDesignBuilder;
export { createEmptyDesign, createDefaultStyleDesign, parseDesignJson, renderDesignToHtml, isDesignTemplate, designToPlainText } from '../utils/emailDesign';
